import React, { useState } from "react";
import {
  FaCheckCircle,
  FaRegCircle,
  FaChevronDown,
  FaChevronUp,
  FaStar,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";
import { toast } from "react-toastify";
import CodeBlock from "../common/CodeBlock";

const NotesViewer = ({
  topic,
  blocks,
  exercises,
  onMarkComplete,
  isCompleted,
  // NEW PROPS for navigation
  hasPrevious = false,
  hasNext = false,
  onPrevious = null,
  onNext = null,
  currentIndex = null,
  totalTopics = null,
}) => {
  const [showSolutions, setShowSolutions] = useState({});
  const [completing, setCompleting] = useState(false);

  const toggleSolution = (exerciseId) => {
    setShowSolutions((prev) => ({
      ...prev,
      [exerciseId]: !prev[exerciseId],
    }));
  };

  const handleMarkComplete = async () => {
    if (completing) return;

    setCompleting(true);
    try {
      await onMarkComplete();
    } catch (error) {
      console.error("Error marking completion:", error);
      toast.error("Failed to update status");
    } finally {
      setCompleting(false);
    }
  };

  const handlePrevious = async () => {
    if (onPrevious && !completing) {
      await onPrevious();
    }
  };

  const handleNext = async () => {
    if (onNext && !completing) {
      await onNext();
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "easy":
        return "#28a745";
      case "medium":
        return "#ffc107";
      case "hard":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  const getDifficultyBadge = (difficulty) => {
    const colors = {
      easy: "easy",
      medium: "medium",
      hard: "hard",
    };
    return (
      <span className={`notes-difficulty-badge ${colors[difficulty]}`}>
        {difficulty}
      </span>
    );
  };

  return (
    <div className="notes-viewer">
      {/* Topic Header with Position Indicator */}
      <div className="notes-viewer-header">
        <div className="notes-viewer-header-top">
          <h1 className="notes-viewer-title">{topic.title}</h1>
          {currentIndex !== null && totalTopics !== null && (
            <span className="notes-position-badge">
              {currentIndex} of {totalTopics}
            </span>
          )}
        </div>
        {topic.description && (
          <p className="notes-viewer-description">{topic.description}</p>
        )}
      </div>

      {/* Content Blocks */}
      <div className="notes-viewer-content">
        {blocks.map((block, index) => (
          <div key={block.id} className="notes-content-block">
            {block.block_type === "explanation" && (
              <div className="notes-explanation">
                {block.title && <h3>{block.title}</h3>}
                <div dangerouslySetInnerHTML={{ __html: block.content }} />
              </div>
            )}

            {block.block_type === "code" && (
              <div className="notes-code-section">
                {block.title && <h4>{block.title}</h4>}
                <CodeBlock
                  code={block.code_content}
                  language={block.language}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Exercises Section */}
      {exercises.length > 0 && (
        <div className="notes-exercises-section">
          <h2 className="notes-exercises-title">
            <FaStar className="notes-exercises-icon" />
            Practice Exercises
          </h2>

          {exercises.map((exercise) => (
            <div key={exercise.id} className="notes-exercise-card">
              <div className="notes-exercise-header">
                <div className="notes-exercise-badge">
                  {getDifficultyBadge(exercise.difficulty)}
                </div>
                <button
                  className="notes-exercise-toggle"
                  onClick={() => toggleSolution(exercise.id)}
                >
                  {showSolutions[exercise.id] ? (
                    <>
                      Hide Solution <FaChevronUp />
                    </>
                  ) : (
                    <>
                      Show Solution <FaChevronDown />
                    </>
                  )}
                </button>
              </div>
              <div className="notes-exercise-question">
                <p>{exercise.question}</p>
              </div>
              {showSolutions[exercise.id] && exercise.solution && (
                <div className="notes-exercise-solution">
                  <div className="notes-solution-header">
                    <strong>📖 Solution:</strong>
                  </div>
                  <CodeBlock
                    code={exercise.solution}
                    language={exercise.solution_language}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Navigation and Complete Button Footer */}
      <div className="notes-viewer-footer">
        <div className="notes-navigation-buttons">
          <button
            className={`notes-nav-btn notes-prev-btn ${!hasPrevious ? "disabled" : ""}`}
            onClick={handlePrevious}
            disabled={!hasPrevious || completing}
          >
            <FaArrowLeft className="me-2" />
            Previous Topic
          </button>

          <button
            className={`notes-complete-btn ${isCompleted ? "completed" : ""}`}
            onClick={handleMarkComplete}
            disabled={completing}
          >
            {isCompleted ? (
              <>
                <FaCheckCircle />
                Completed
              </>
            ) : (
              <>
                <FaRegCircle />
                Mark as Completed
              </>
            )}
          </button>

          <button
            className={`notes-nav-btn notes-next-btn ${!hasNext ? "disabled" : ""}`}
            onClick={handleNext}
            disabled={!hasNext || completing}
          >
            Next Topic
            <FaArrowRight className="ms-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotesViewer;